import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Clock, Check, X as XIcon, Eye, EyeOff, Trash2, Pencil, GripVertical, Copy } from 'lucide-react';
import { StockMatrix } from './stockMatrices';

export const InventoryProductCard = ({ product, saving, onPatch, onEdit, onDelete, onDuplicate, onMove, isFirst, isLast, selectMode, isSelected, onToggleSelect, onDragStartCard, onDragEndCard, onTouchDragStart, onTouchDragMove, onTouchDrop }) => {
  const canDelete = product.product_id >= 17;
  const [dragEnabled, setDragEnabled] = useState(false);
  const sizes = product.sizes?.es || [];
  const isSaving = saving === product.product_id;
  const showThreads = !product.no_tzitzit && !product.is_promo;
  const colors = product.colors || [];

  // Detect if product has BOTH kid + adult sizes (to expose separate kids price input)
  const hasKidSizes = sizes.some(s => s.startsWith('Niño') || s.startsWith('Boys') || s === 'XL Boys');
  const hasAdultSizes = sizes.some(s => s.startsWith('Adulto') || s.startsWith('Adult'));
  const showKidsPriceInput = hasKidSizes && hasAdultSizes && !product.pricing_rules;

  // Stock summary for badge
  const stockQty = product.stock_qty || {};
  const threshold = product.low_stock_threshold ?? 3;
  const totalStock = Object.values(stockQty).reduce((s, v) => s + (parseInt(v, 10) || 0), 0);
  const lowCells = Object.values(stockQty).filter(v => {
    const n = parseInt(v, 10) || 0;
    return n > 0 && n <= threshold;
  }).length;

  // Visibility summary (only show when admin has hidden something)
  const hiddenColors = product.hidden_colors || [];
  const hiddenTraditions = product.hidden_traditions || [];
  const hiddenThreads = product.hidden_threads || [];
  const hiddenCottonTypes = product.hidden_cotton_types || [];
  const visColorsCount = colors.length - hiddenColors.filter(v => colors.some(c => c.value === v)).length;
  const visTraditionsCount = 3 - hiddenTraditions.length;
  const visThreadsCount = 3 - hiddenThreads.length;
  const visCottonCount = 2 - hiddenCottonTypes.length;
  const visibilityBadges = [];
  if (colors.length > 1 && hiddenColors.length > 0) {
    visibilityBadges.push({ label: `${visColorsCount}/${colors.length} colores`, key: 'colors' });
  }
  if (showThreads && hiddenTraditions.length > 0) {
    visibilityBadges.push({ label: `${visTraditionsCount}/3 tradiciones`, key: 'trad' });
  }
  if (showThreads && hiddenThreads.length > 0) {
    visibilityBadges.push({ label: `${visThreadsCount}/3 hilos`, key: 'hilos' });
  }
  if (product.has_cotton_type && hiddenCottonTypes.length > 0) {
    visibilityBadges.push({ label: `${visCottonCount}/2 algodón`, key: 'cotton' });
  }

  return (
    <Card
      className="bg-white overflow-hidden"
      data-testid={`inv-product-${product.product_id}`}
      draggable={dragEnabled}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStartCard?.(); }}
      onDragEnd={() => { setDragEnabled(false); onDragEndCard?.(); }}
    >
      <div className="px-3 py-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {selectMode && (
              <input
                type="checkbox"
                checked={!!isSelected}
                disabled={!canDelete}
                onChange={() => onToggleSelect?.(product.product_id)}
                title={canDelete ? 'Seleccionar para eliminar' : 'Producto base protegido'}
                className="w-4 h-4 shrink-0 accent-red-600 disabled:opacity-30 cursor-pointer"
                data-testid={`select-product-${product.product_id}`}
              />
            )}
            <div
              className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600 shrink-0 touch-none -ml-1"
              onMouseDown={() => setDragEnabled(true)}
              onMouseUp={() => setDragEnabled(false)}
              onTouchStart={() => onTouchDragStart?.()}
              onTouchMove={(e) => { const t = e.touches[0]; onTouchDragMove?.(t.clientX, t.clientY); }}
              onTouchEnd={() => onTouchDrop?.()}
              title="Arrastrar para reordenar"
              data-testid={`drag-handle-${product.product_id}`}
            >
              <GripVertical className="w-4 h-4" />
            </div>
            <div className="flex flex-col shrink-0 -my-1 -ml-1.5">
              <button onClick={() => onMove?.(product.product_id, -1)} disabled={isFirst || isSaving}
                className="text-slate-300 hover:text-slate-700 disabled:opacity-20 text-xs leading-none py-0.5"
                title="Subir en el catálogo" data-testid={`move-up-${product.product_id}`}>▲</button>
              <button onClick={() => onMove?.(product.product_id, 1)} disabled={isLast || isSaving}
                className="text-slate-300 hover:text-slate-700 disabled:opacity-20 text-xs leading-none py-0.5"
                title="Bajar en el catálogo" data-testid={`move-down-${product.product_id}`}>▼</button>
            </div>
            <img src={product.image} alt="" className="w-10 h-10 sm:w-9 sm:h-9 object-contain rounded bg-slate-50 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-slate-800 leading-tight break-words sm:truncate">{product.name?.es || 'Sin nombre'}</p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap leading-tight mt-0.5">
                <span className="capitalize">{product.material} · {product.style}</span>
                {totalStock > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">
                    📦 {totalStock}
                  </span>
                )}
                {lowCells > 0 && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold animate-pulse"
                    data-testid={`low-stock-badge-${product.product_id}`}
                    title={`${lowCells} talla(s) con stock bajo (≤ ${threshold})`}
                  >
                    ⚠️ Bajo: {lowCells}
                  </span>
                )}
                {visibilityBadges.map((b) => (
                  <span
                    key={b.key}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold"
                    title="Opciones ocultas al cliente"
                    data-testid={`visibility-badge-${product.product_id}-${b.key}`}
                  >
                    👁️‍🗨️ {b.label}
                  </span>
                ))}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap sm:flex-nowrap sm:shrink-0 justify-end">
            {showKidsPriceInput ? (
              <div className="flex items-center gap-1 bg-slate-50 rounded px-1.5 py-0.5 border border-slate-200">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Adulto</span>
                <span className="text-[11px] text-slate-500">$</span>
                <input type="number" defaultValue={product.price_mxn}
                  className="w-16 text-xs text-right border border-slate-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  onBlur={(e) => { const v = parseFloat(e.target.value); if (v && v !== product.price_mxn) onPatch(product.product_id, { price_mxn: v }, { price_mxn: v }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  data-testid={`price-input-${product.product_id}`} />
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide ml-1">Niño</span>
                <span className="text-[11px] text-slate-500">$</span>
                <input type="number" defaultValue={product.kids_price_mxn ?? ''}
                  placeholder="—"
                  className="w-16 text-xs text-right border border-slate-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const v = raw === '' ? null : parseFloat(raw);
                    if (v === null || (v >= 0 && v !== product.kids_price_mxn)) {
                      onPatch(product.product_id, { kids_price_mxn: v }, { kids_price_mxn: v });
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  data-testid={`kids-price-input-${product.product_id}`} />
                <span className="text-[10px] text-slate-400">MXN</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500">$</span>
                <input type="number" defaultValue={product.price_mxn}
                  className="w-20 text-xs text-right border border-slate-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  onBlur={(e) => { const v = parseFloat(e.target.value); if (v && v !== product.price_mxn) onPatch(product.product_id, { price_mxn: v }, { price_mxn: v }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  data-testid={`price-input-${product.product_id}`} />
                <span className="text-[10px] text-slate-400">MXN</span>
              </div>
            )}
            <button onClick={() => onPatch(product.product_id, { in_stock: !product.in_stock }, { in_stock: !product.in_stock })}
              disabled={isSaving}
              title={product.in_stock ? 'En Stock' : 'Agotado'}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-all ${product.in_stock ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
              data-testid={`stock-toggle-${product.product_id}`}>
              {product.in_stock ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
              <span className="hidden sm:inline">{product.in_stock ? 'En Stock' : 'Agotado'}</span>
            </button>
            <button onClick={() => onPatch(product.product_id, { hidden: !product.hidden }, { hidden: !product.hidden })}
              disabled={isSaving}
              title={product.hidden ? 'Oculto · click para mostrar' : 'Visible · click para ocultar'}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-all ${product.hidden ? 'bg-slate-800 text-white hover:bg-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`hidden-toggle-${product.product_id}`}>
              {product.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              <span className="hidden sm:inline">{product.hidden ? 'Oculto' : 'Visible'}</span>
            </button>
            <button onClick={() => onPatch(product.product_id, { coming_soon: !product.coming_soon }, { coming_soon: !product.coming_soon })}
              disabled={isSaving}
              title={product.coming_soon ? 'Próximamente · click para liberar' : 'Disponible · click para marcar Prox.'}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-all ${product.coming_soon ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`coming-soon-toggle-${product.product_id}`}>
              <Clock className="w-3 h-3" />
              <span className="hidden sm:inline">{product.coming_soon ? 'Próximamente' : 'Disponible'}</span>
            </button>
            {onEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(product); }}
                className="p-1 rounded text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 transition-colors"
                title="Editar producto (nombre, fotos, descripción...)"
                data-testid={`edit-product-${product.product_id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {onDuplicate && (
              <button
                onClick={(e) => { e.stopPropagation(); onDuplicate(product.product_id); }}
                disabled={isSaving}
                className="p-1 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors disabled:opacity-40"
                title="Duplicar producto (se crea oculto con stock en 0)"
                data-testid={`duplicate-product-${product.product_id}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            )}
            {onDelete && canDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(product.product_id); }}
                className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Eliminar producto"
                data-testid={`delete-product-${product.product_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {sizes.length > 0 && (
        <div className="border-t border-slate-100 px-3 py-1.5 bg-slate-50/60">
          <StockMatrix product={product} onPatch={onPatch} isSaving={isSaving} />
        </div>
      )}
    </Card>
  );
};

export const PricingRulesEditor = ({ product, onPatch, isSaving }) => {
  const pr = product.pricing_rules || {};
  const kidsThreads = pr.kids_threads || [];
  const adultThreads = pr.adult_threads || [];
  const kidsPrices = pr.kids_prices_mxn || {};
  const adultPrices = pr.adult_prices_mxn || {};
  const baseKidsPrice = pr.kids_price_mxn ?? null;

  const labels = { avodat_yad_grueso: 'A Mano Gruesos', avodat_yad_delgado: 'A Mano Delgados', sin_hilos: 'Sin Hilos', mechona_delgado: 'A Máquina' };

  const patchPath = (path, value) => {
    const newPR = JSON.parse(JSON.stringify(pr));
    if (path === 'kids_price_mxn') {
      newPR.kids_price_mxn = value;
    } else {
      const [outer, inner] = path.split('.');
      newPR[outer] = { ...(newPR[outer] || {}), [inner]: value };
    }
    onPatch(product.product_id, { pricing_rules: newPR }, { pricing_rules: newPR });
  };

  const PriceInput = ({ value, onCommit, testId }) => (
    <div className="flex items-center gap-1 bg-white rounded border border-slate-200 px-1.5 py-0.5">
      <span className="text-[10px] text-slate-400">$</span>
      <input
        type="number"
        defaultValue={value ?? ''}
        className="w-16 text-xs text-right focus:outline-none"
        onBlur={(e) => {
          const raw = e.target.value.trim();
          if (raw === '') return;
          const v = parseFloat(raw);
          if (v >= 0 && v !== value) onCommit(v);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        disabled={isSaving}
        data-testid={testId}
      />
      <span className="text-[9px] text-slate-400">MXN</span>
    </div>
  );

  const kidsThreadKeys = Array.from(new Set([...kidsThreads, ...Object.keys(kidsPrices)]));
  const kidsRows = kidsThreadKeys.map((thr) => {
    const hasOverride = kidsPrices[thr] !== undefined;
    return {
      thread: thr,
      value: hasOverride ? kidsPrices[thr] : baseKidsPrice,
      path: hasOverride ? `kids_prices_mxn.${thr}` : 'kids_price_mxn',
      isBase: !hasOverride,
    };
  });

  return (
    <div className="bg-cyan-50/50 border border-cyan-200 rounded-lg p-2.5" data-testid={`pricing-rules-editor-${product.product_id}`}>
      <p className="text-xs font-bold text-cyan-800 mb-2 flex items-center gap-1.5">
        <span>💰</span> Precios por hilo (Niño / Adulto)
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Niño</p>
          <div className="space-y-1.5">
            {kidsRows.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic">Sin tallas de niño</p>
            ) : kidsRows.map((row) => (
              <div key={row.thread} className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-700 truncate" title={labels[row.thread] || row.thread}>
                  {labels[row.thread] || row.thread}
                  {row.isBase && <span className="ml-1 text-[9px] text-slate-400">(base)</span>}
                </span>
                <PriceInput
                  value={row.value}
                  onCommit={(v) => patchPath(row.path, v)}
                  testId={`pricing-rules-kids-${product.product_id}-${row.thread}`}
                />
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Adulto</p>
          <div className="space-y-1.5">
            {adultThreads.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic">Sin tallas de adulto</p>
            ) : adultThreads.map((thr) => (
              <div key={thr} className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-700 truncate" title={labels[thr] || thr}>
                  {labels[thr] || thr}
                </span>
                <PriceInput
                  value={adultPrices[thr]}
                  onCommit={(v) => patchPath(`adult_prices_mxn.${thr}`, v)}
                  testId={`pricing-rules-adult-${product.product_id}-${thr}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-slate-500 mt-2">
        Editar cualquier precio actualiza automáticamente el rango "Desde $X" del catálogo.
      </p>
    </div>
  );
};
